// Intent: [Branding] [Conversion] [Journey]
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { MenuIcon, XIcon } from "lucide-react";
import { PORTAL_LOGIN_URL, PORTAL_REGISTER_URL, SITE_NAVIGATION } from "../../config";
import { BrandLogo } from "../shared/BrandLogo";
import { ButtonLink } from "../shared/ButtonLink";
function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    if (menuOpen) requestAnimationFrame(() => menuRef.current?.querySelector<HTMLElement>("a[href], button")?.focus());
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);
  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setMenuOpen(false);
      requestAnimationFrame(() => menuButtonRef.current?.focus());
      return;
    }
    if (event.key !== "Tab" || !menuRef.current) return;
    const focusable = Array.from(menuRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return <header className="sticky top-0 z-50 border-b border-pfpc-navy/12 bg-pfpc-offwhite/95 shadow-[0_8px_24px_rgba(23,40,59,.04)] backdrop-blur-sm" data-id="element-78" data-mp="3p230nspkr71o">
      <div className="container-shell flex h-[68px] min-w-0 items-center justify-between gap-4 sm:h-[72px] xl:grid xl:h-[76px] xl:grid-cols-[194px_minmax(0,1fr)_auto] xl:gap-5" data-id="element-79" data-mp="1wlu58siijc7">
        <a href="/" aria-label="Perth Flat Pack Cabinets home" className="block w-fit shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pfpc-orange focus-visible:ring-offset-2" data-id="element-80" data-mp="19c8cbi4qi8ti">
          <BrandLogo data-id="element-81" data-mp="35dagumlznc31" />
        </a>

        <nav aria-label="Primary navigation" className="hidden min-w-0 items-center justify-center gap-4 xl:flex 2xl:gap-7" data-id="element-82" data-mp="35e3gl3mj51bo">
          {SITE_NAVIGATION.map(item => <a key={item.href} href={item.href} className="nav-link inline-flex min-h-11 items-center whitespace-nowrap py-3 text-[13px] font-semibold text-pfpc-navy 2xl:text-[14px]" data-id="element-83" data-mp="3ee8967n2dv5s">
              {item.label}
            </a>)}
        </nav>

        <div className="hidden shrink-0 items-center justify-self-end gap-3 xl:flex 2xl:gap-4" data-id="element-84" data-mp="2modmkiqlcjkt">
          <a href={PORTAL_LOGIN_URL} data-analytics="portal_login_clicked" className="nav-link min-h-11 whitespace-nowrap py-3 text-sm font-bold text-pfpc-navy" data-id="element-85" data-mp="30fs4em8rieqa">
            Log In
          </a>
          <ButtonLink href={PORTAL_REGISTER_URL} analytics="start_design_clicked" className="whitespace-nowrap px-4 2xl:px-5" data-id="element-86" data-mp="27wle8qp7s5sl">
            Start Designing Online
          </ButtonLink>
        </div>

        <button ref={menuButtonRef} type="button" aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"} aria-expanded={menuOpen} aria-controls="mobile-menu" onClick={() => setMenuOpen(open => !open)} className="grid h-11 w-11 shrink-0 place-items-center rounded-[10px] border border-pfpc-navy/20 text-pfpc-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pfpc-orange xl:hidden" data-id="element-87" data-mp="ifzf05mtz908">
          {menuOpen ? <XIcon className="h-5 w-5" data-id="element-88" data-mp="165k7pg8sdkmp" /> : <MenuIcon className="h-5 w-5" data-id="element-89" data-mp="s6kjuat3l9rl" />}
        </button>
      </div>

      {menuOpen ? <div ref={menuRef} id="mobile-menu" role="dialog" aria-modal="true" aria-label="Site navigation" onKeyDown={handleMenuKeyDown} className="absolute inset-x-0 top-full z-50 h-[calc(100dvh-68px)] overflow-y-auto bg-pfpc-offwhite p-6 sm:h-[calc(100dvh-72px)] xl:hidden" data-id="element-90" data-mp="x5oruy4ad1gc">
          <nav aria-label="Mobile navigation" className="mx-auto flex max-w-xl flex-col" data-id="element-91" data-mp="3hx7bekw1kxee">
            {SITE_NAVIGATION.map((item, index) => <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)} className="flex min-h-14 items-center justify-between border-b border-pfpc-navy/12 py-3 font-heading text-xl font-bold text-pfpc-navy" data-id="element-92" data-mp="3seaw1g6y0uy8" data-mpi={index}>
                {item.label}<span className="text-xs font-semibold text-pfpc-orange" data-id="element-93" data-mp="3jffu4gaxm70q">0{index + 1}</span>
              </a>)}
            <div className="mt-7 grid gap-3" data-id="element-94" data-mp="jwwx2a3knqzo">
              <ButtonLink href={PORTAL_REGISTER_URL} analytics="start_design_clicked" className="w-full" data-id="element-95" data-mp="fx5znnjjicm6">Start Designing Online</ButtonLink>
              <ButtonLink href={PORTAL_LOGIN_URL} variant="secondary" analytics="portal_login_clicked" className="w-full" data-id="element-96" data-mp="awhzab9f8jfa">Already registered? Log in</ButtonLink>
            </div>
          </nav>
        </div> : null}
    </header>;
}
export { Header };